import { Box, Divider, Grid } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, useEffect } from "react";
import { Plus } from "lucide-react";

import { TextField, Autocomplete, Button, Alert } from "../../../../../../shared";
import SideModal from "../../../../../../components/SideModal";
import { useLabor } from "../../../../../../hooks/query";
import { useNotification } from "../../../../../../hooks";
import laborInventoryResolver from "../../../../../../libs/form-validations/labor-inventory";

interface LaborRateFormProps {
    supplierId: string;
    rateItem?: any | null;
    onClose: () => void;
}

export function LaborRateForm({ supplierId, rateItem, onClose }: LaborRateFormProps) {
    const queryClient = useQueryClient();
    const [submissionError, setSubmissionError] = useState<any>(null);
    const { showSuccess } = useNotification();
    const { 
        useListLaborServices, 
        useListLaborInventory, 
        useCreateLaborInventory, 
        useUpdateLaborInventory, 
        useDeleteLaborInventory 
    } = useLabor();
    
    const { data: servicesData } = useListLaborServices();
    const { data: existingRatesData } = useListLaborInventory({ supplierId });
    const createRateSvc = useCreateLaborInventory();
    const updateRateSvc = useUpdateLaborInventory();
    const deleteRateSvc = useDeleteLaborInventory();

    const isEditing = !!rateItem;
    const services = (servicesData as any[]) || [];
    const existingRates = (existingRatesData as any[]) || [];

    const serviceOptions = useMemo(
        () => services.map((service) => ({
            value: service.Id,
            label: `${service.Name} (${service.Unit})`,
        })),
        [services]
    );

    const methods = useForm({
        resolver: laborInventoryResolver,
        defaultValues: {
            LaborServiceId: rateItem?.LaborService_Id || "",
            UnitPrice: rateItem?.UnitPrice ?? 0,
            IsActive: rateItem?.IsActive ?? 1,
        },
        mode: "onBlur",
    });

    const [selectedService, setSelectedService] = useState<{ value: string; label: string } | null>(
        rateItem ? { value: rateItem.LaborService_Id, label: `${rateItem.LaborServiceName} (${rateItem.Unit})` } : null
    );

    const onSubmit = methods.handleSubmit(async (data) => {
        setSubmissionError(null);
        try {
            const laborServiceId = selectedService?.value || (typeof data.LaborServiceId === "string" ? data.LaborServiceId : "");
            
            if (!laborServiceId) {
                setSubmissionError("Labor service is required");
                return;
            }

            // Check for duplicate
            const isDuplicate = existingRates.some(rate => 
                rate.LaborService_Id === laborServiceId && (!isEditing || rate.Id !== rateItem.Id)
            );

            if (isDuplicate) {
                methods.setError("LaborServiceId", { message: "This labor service already has a rate for this supplier." });
                setSubmissionError("This labor service already has a rate for this supplier.");
                return;
            }

            if (isEditing) {
                await updateRateSvc.mutateAsync({
                    Id: rateItem.Id,
                    UnitPrice: data.UnitPrice,
                    IsActive: data.IsActive,
                });
                showSuccess("Labor rate updated successfully");
            } else {
                await createRateSvc.mutateAsync({
                    SupplierId: supplierId,
                    LaborServiceId: laborServiceId,
                    UnitPrice: data.UnitPrice,
                    IsActive: data.IsActive,
                });
                showSuccess("Labor rate created successfully");
            }

            queryClient.invalidateQueries({ queryKey: ["labor-inventory", { supplierId }] });
            onClose();
        } catch (error: any) {
            console.error("Failed to save labor rate:", error);
            setSubmissionError(error);
        }
    });

    const handleDelete = async () => {
        if (!rateItem?.Id) return;
        setSubmissionError(null);
        try {
            await deleteRateSvc.mutateAsync(rateItem.Id);
            queryClient.invalidateQueries({ queryKey: ["labor-inventory", { supplierId }] });
            showSuccess("Labor rate deleted successfully");
            onClose();
        } catch (error: any) {
            console.error("Failed to delete labor rate:", error);
            setSubmissionError(error);
        }
    };

    const isLoading = createRateSvc.isPending || updateRateSvc.isPending || deleteRateSvc.isPending;

    return (
        <SideModal
            open={true}
            onClose={onClose}
            title={isEditing ? "Edit Labor Rate" : "Add Labor Rate"}
            description={isEditing ? "Update the pricing for this labor service." : "Set a new price for a labor service."}
        >
            <FormProvider {...methods}>
                <form onSubmit={onSubmit}>
                    <Box display="flex" flexDirection="column" gap={3} p={3}>
                        <Autocomplete
                            name="LaborServiceId"
                            label="Labor Service"
                            helperText="Select the service you want to set a price for"
                            placeholder="Select service..."
                            options={serviceOptions}
                            value={selectedService}
                            disabled={isEditing}
                            onChange={(e: any, value: any) => {
                                setSelectedService(value);
                                methods.setValue("LaborServiceId", value || "", { shouldDirty: true });
                            }}
                        />

                        <TextField
                            name="UnitPrice"
                            label="Unit Price ($)"
                            helperText="Set the price for this labor service"
                            placeholder="e.g., 140.00"
                            type="number"
                        />

                        <Alert 
                            errorMessage={submissionError}
                            sx={{ mt: 1 }}
                        />

                        <Divider sx={{ my: 1 }} />

                        <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button variant="outlined" color="neutral" onClick={onClose} disabled={isLoading}>
                                Cancel
                            </Button>
                            {isEditing && (
                                <Button variant="soft" color="danger" onClick={handleDelete} disabled={isLoading}>
                                    Delete
                                </Button>
                            )}
                            <Button
                                type="submit"
                                disabled={isLoading || (!methods.formState.isDirty && !isEditing)}
                                loading={isLoading}
                                variant="soft"
                                color="primary"
                            >
                                {isEditing ? "Update Rate" : "Add Rate"}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </FormProvider>
        </SideModal>
    );
}
